import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { ArrowRight, Drama, Mic, MessageCircle, GraduationCap, School } from "lucide-react";
import { Navbar } from "../components/Navbar";
import { Footer } from "../components/Footer";
import { PostCard } from "../components/PostCard";
import { api } from "../lib/api";

const HERO_IMG =
  "https://images.unsplash.com/photo-1562329265-95a6d7a83440?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjAzMzl8MHwxfHNlYXJjaHwyfHx0aGVhdHJlJTIwc3RhZ2UlMjBzcG90bGlnaHR8ZW58MHx8fHwxNzgzMjIzNTk5fDA&ixlib=rb-4.1.0&q=85";
const PORTRAIT_IMG = "https://customer-assets-wrfwihn1.emergentagent.net/job_speech-therapy-lab/artifacts/60df0c6750f62473_IMG_9069.jpeg";

const services = [
  { icon: Drama, title: "Drama Coaching", desc: "Audition prep, monologue work, character and stagecraft for stage and screen.", span: "md:col-span-7", tall: true },
  { icon: GraduationCap, title: "LAMDA Exam Prep", desc: "Structured, confidence-building preparation for graded LAMDA examinations.", span: "md:col-span-5" },
  { icon: Mic, title: "Voice & Communication", desc: "Breath, resonance, projection and clarity for speakers and performers.", span: "md:col-span-5" },
  { icon: MessageCircle, title: "Speech Therapy", desc: "Compassionate, evidence-informed support for speech and communication (student practitioner).", span: "md:col-span-7", tall: true },
  { icon: School, title: "Drama School Coaching", desc: "Preparation and support for degree-level acting courses, from drama school auditions and recalls to coursework and monologues.", span: "md:col-span-12" },
];

const marqueeItems = [
  "LAMDA", "Voice & Communication", "Speech Therapy Student",
  "Audition Coaching", "Public Speaking", "Confidence Building",
];

export default function Home() {
  const [reviews, setReviews] = useState([]);
  const [blogs, setBlogs] = useState([]);

  useEffect(() => {
    api.get("/posts", { params: { type: "review" } }).then((r) => setReviews(r.data.slice(0, 3))).catch(() => {});
    api.get("/posts", { params: { type: "blog" } }).then((r) => setBlogs(r.data.slice(0, 3))).catch(() => {});
  }, []);

  return (
    <div>
      <Navbar />

      {/* HERO */}
      <section className="relative min-h-screen flex items-end overflow-hidden">
        <div className="absolute inset-0">
          <img src={HERO_IMG} alt="Dramatic red theatre curtain with stage spotlight" className="w-full h-full object-cover" />
          <div className="absolute inset-0 bg-gradient-to-t from-[#1A1A1A] via-[#1A1A1A]/50 to-[#1A1A1A]/30" />
        </div>
        <div className="relative max-w-7xl mx-auto px-6 md:px-12 pb-24 pt-32 w-full">
          <motion.p
            initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.6 }}
            className="label-caps text-[#D4AF37] mb-6"
          >
            Drama · Voice · Communication · Speech Therapy
          </motion.p>
          <motion.h1
            initial={{ opacity: 0, y: 30 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.7, delay: 0.1 }}
            className="font-serif-display text-5xl sm:text-6xl lg:text-7xl font-light leading-[0.95] text-[#F7F5F0] max-w-4xl"
          >
            Every voice has a story.<br />
            <span className="italic">Let's help yours be heard.</span>
          </motion.h1>
          <motion.p
            initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.6, delay: 0.3 }}
            className="text-lg text-[#F7F5F0]/80 max-w-xl mt-6 leading-relaxed"
          >
            Coaching for performers, students and speakers, from LAMDA exams and audition
            prep to voice, communication and speech development.
          </motion.p>
          <motion.div
            initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.6, delay: 0.45 }}
            className="flex flex-wrap gap-4 mt-10"
          >
            <Link to="/contact" data-testid="hero-book-button" className="bg-[#7A1C28] text-white hover:bg-[#5C151E] transition-colors px-8 py-4 label-caps text-[0.7rem] flex items-center gap-2">
              Book a Session <ArrowRight size={16} />
            </Link>
            <Link to="/reviews" data-testid="hero-reviews-button" className="border border-[#F7F5F0]/40 text-[#F7F5F0] hover:bg-[#F7F5F0] hover:text-[#1A1A1A] transition-colors px-8 py-4 label-caps text-[0.7rem]">
              Read Theatre Reviews
            </Link>
          </motion.div>
        </div>
      </section>

      {/* MARQUEE */}
      <section className="bg-[#1A1A1A] py-5 overflow-hidden">
        <div className="marquee-track">
          {[...marqueeItems, ...marqueeItems].map((item, i) => (
            <span key={i} className="font-serif-display text-2xl text-[#F7F5F0]/70 mx-8 whitespace-nowrap flex items-center gap-8">
              {item} <span className="text-[#D4AF37]">✦</span>
            </span>
          ))}
        </div>
      </section>

      {/* SERVICES */}
      <section id="services" className="max-w-7xl mx-auto px-6 md:px-12 py-24">
        <div className="max-w-2xl mb-16">
          <p className="label-caps text-[#7A1C28] mb-4">What I offer</p>
          <h2 className="font-serif-display text-4xl sm:text-5xl font-light leading-tight">
            Tailored coaching across the spoken word.
          </h2>
        </div>
        <div className="grid grid-cols-1 md:grid-cols-12 gap-6">
          {services.map((s, i) => {
            const Icon = s.icon;
            return (
              <motion.div
                key={s.title}
                data-testid={`service-card-${i}`}
                initial={{ opacity: 0, y: 30 }} whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }} transition={{ duration: 0.5, delay: i * 0.1 }}
                className={`${s.span} ${s.tall ? "md:py-16" : "md:py-10"} bg-[#FFFFFF] border border-[#E5E2D9] p-8 md:p-12 flex flex-col justify-between transition-all duration-300 hover:-translate-y-1 hover:shadow-md`}
              >
                <Icon size={32} className="text-[#7A1C28] mb-8" strokeWidth={1.4} />
                <div>
                  <h3 className="font-serif-display text-2xl md:text-3xl mb-3">{s.title}</h3>
                  <p className="text-[#4A4A4A] leading-relaxed">{s.desc}</p>
                </div>
              </motion.div>
            );
          })}
        </div>
      </section>

      {/* ABOUT */}
      <section className="bg-[#EFEBE1]">
        <div className="max-w-7xl mx-auto px-6 md:px-12 py-24 grid grid-cols-1 md:grid-cols-12 gap-12 items-center">
          <div className="md:col-span-5">
            <div className="aspect-[4/5] overflow-hidden border border-[#E5E2D9]">
              <img src={PORTRAIT_IMG} alt="The coach speaking" className="w-full h-full object-cover" />
            </div>
          </div>
          <div className="md:col-span-7 md:pl-8">
            <p className="label-caps text-[#7A1C28] mb-4">About</p>
            <h2 className="font-serif-display text-4xl sm:text-5xl font-light leading-tight mb-6">
              A working actor and student speech therapist.
            </h2>
            <p className="text-[#4A4A4A] leading-relaxed mb-4">
              I'm Jack Pritchard, an articulate, sensitive and grounded actor, coach and
              student speech &amp; language therapist. Born and raised in the Midlands, I
              trained on BA Acting at Collective Acting Studio and Bristol Old Vic Theatre
              School. I'm currently completing my BSc in Speech &amp; Language Therapy
              at the University of Manchester (graduating 2027).
            </p>
            <p className="text-[#4A4A4A] leading-relaxed mb-4">
              As a professional actor I've performed with the Donmar Warehouse, P&amp;O Cruises
              and the Minack Theatre. I facilitated at Nottingham Playhouse for over two years,
              starting as an assistant and ending leading a programme there. I also teach for
              Stagecoach, Pauline Quirke Academy and Rattle n Roll. I also tour one-person shows
              into primary schools with Freshwater Theatre Company and into nurseries with
              Twinkleboost, integrating Makaton in their performances.
            </p>
            <p className="text-[#4A4A4A] leading-relaxed mb-8">
              My coaching brings together the instincts of a professional performer and the
              rigour of speech therapy: helping you build genuine confidence, find truth in
              your performance, and pass your exams first time. I'm based in the North West,
              with an additional in-person teaching base near Lichfield, and I also coach online.
            </p>
            <Link to="/contact" data-testid="about-cta" className="inline-flex items-center gap-2 border border-[#1A1A1A] text-[#1A1A1A] hover:bg-[#1A1A1A] hover:text-[#F7F5F0] transition-colors px-8 py-4 label-caps text-[0.7rem]">
              Work with me <ArrowRight size={16} />
            </Link>
          </div>
        </div>
      </section>

      {/* REVIEWS PREVIEW */}
      {reviews.length > 0 && (
        <section className="max-w-7xl mx-auto px-6 md:px-12 py-24">
          <div className="flex items-end justify-between mb-12 flex-wrap gap-4">
            <div>
              <p className="label-caps text-[#7A1C28] mb-4">From the stalls</p>
              <h2 className="font-serif-display text-4xl sm:text-5xl font-light">Theatre Reviews</h2>
            </div>
            <Link to="/reviews" data-testid="all-reviews-link" className="label-caps text-[0.7rem] inline-flex items-center gap-2 hover:text-[#7A1C28] transition-colors">
              All reviews <ArrowRight size={16} />
            </Link>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            {reviews.map((p, i) => <PostCard key={p.id} post={p} index={i} />)}
          </div>
        </section>
      )}

      {/* BLOG PREVIEW */}
      {blogs.length > 0 && (
        <section className="bg-[#EFEBE1]">
          <div className="max-w-7xl mx-auto px-6 md:px-12 py-24">
            <div className="flex items-end justify-between mb-12 flex-wrap gap-4">
              <div>
                <p className="label-caps text-[#7A1C28] mb-4">Learn &amp; grow</p>
                <h2 className="font-serif-display text-4xl sm:text-5xl font-light">Blog &amp; Resources</h2>
              </div>
              <Link to="/blog" data-testid="all-blog-link" className="label-caps text-[0.7rem] inline-flex items-center gap-2 hover:text-[#7A1C28] transition-colors">
                All articles <ArrowRight size={16} />
              </Link>
            </div>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
              {blogs.map((p, i) => <PostCard key={p.id} post={p} index={i} />)}
            </div>
          </div>
        </section>
      )}

      <Footer />
    </div>
  );
}
